// web/src/lib/records.ts — small, presentation-only derivations shared across the
// M4 records surfaces (dashboard, detail, responsibilities). METADATA ONLY: these
// helpers read counts/statuses/timestamps from the read-path types and never touch
// shard ciphertext, a key, or plaintext (R11). Words for every state come from
// design/content-guide.md §11 (security-state badges) — no improvised microcopy.
import type {
  PossessionStatus,
  RecordMemberView,
  RecordSummary,
  SecretRecordStatus,
  UnsealRequest,
  UnsealStatus,
} from '../../../shared/types';
import type { Tone } from '../components/ui';

/** A derived security-state badge for a record row (content-guide §11). */
export interface HealthBadge {
  label: string;
  tone: Tone;
  tooltip: string;
}

/**
 * Derive a record's quorum-health badge from its metadata. The cascade mirrors the
 * count-driven StatusBadge in the wireframes: a live ceremony wins, then proof gaps,
 * then a no-margin (K = N) note, else healthy. `provenCount` is how many members
 * have a fresh possession proof; `memberCount` is the live (non-revoked) slot count.
 */
export function quorumHealth(
  r: Pick<RecordSummary, 'status' | 'thresholdK' | 'totalN' | 'memberCount' | 'provenCount'>,
  hasLiveCeremony = false,
): HealthBadge {
  if (hasLiveCeremony) {
    return {
      label: 'Breaking glass',
      tone: 'info',
      tooltip: 'An unseal ceremony is in progress.',
    };
  }
  if (r.status === 'revoked') {
    return { label: 'Revoked', tone: 'danger', tooltip: 'This record has been revoked.' };
  }
  if (r.status === 'redealing') {
    return {
      label: 'Re-deal needed',
      tone: 'warning',
      tooltip: 'Membership or keys changed. Re-deal to restore a healthy quorum.',
    };
  }
  const gap = r.memberCount - r.provenCount;
  if (gap > 0) {
    return {
      label: 'Proof overdue',
      tone: 'danger',
      tooltip: `${gap} member${gap === 1 ? '' : 's'} haven't confirmed possession of their shard.`,
    };
  }
  if (r.thresholdK >= r.totalN) {
    return {
      label: 'No margin',
      tone: 'warning',
      tooltip: `Every member is needed to unseal (${r.thresholdK} of ${r.totalN}). There's no redundancy.`,
    };
  }
  return {
    label: 'Quorum healthy',
    tone: 'success',
    tooltip: `${r.thresholdK} of ${r.totalN} members can act. All recent possession proofs passed.`,
  };
}

/** Map a record lifecycle status to a badge tone + label. */
export function statusBadge(status: SecretRecordStatus): { label: string; tone: Tone } {
  switch (status) {
    case 'sealed':
      return { label: 'Sealed', tone: 'neutral' };
    case 'redealing':
      return { label: 'Re-dealing', tone: 'warning' };
    case 'revoked':
      return { label: 'Revoked', tone: 'danger' };
    default:
      return { label: status, tone: 'neutral' };
  }
}

/** The caller's role on a record, for the "Role" column. */
export function roleLabel(r: Pick<RecordSummary, 'isOwner' | 'isHolder'>): string {
  if (r.isOwner && r.isHolder) return 'owner · shard';
  if (r.isOwner) return 'owner';
  if (r.isHolder) return 'shard';
  return 'member';
}

/** Possession-proof status → badge (content-guide §6.5 / §11). */
export function possessionBadge(s: PossessionStatus | null): { label: string; tone: Tone } {
  switch (s) {
    case 'proven':
      return { label: 'Fresh', tone: 'success' };
    case 'pending':
      return { label: 'Pending', tone: 'neutral' };
    case 'overdue':
      return { label: 'Overdue', tone: 'warning' };
    case 'failed':
      return { label: 'Failed', tone: 'danger' };
    default:
      return { label: 'No proof yet', tone: 'neutral' };
  }
}

/** A ceremony counts as live (open) while it is still gathering shards. */
export function isLiveCeremony(c: Pick<UnsealRequest, 'status'>): boolean {
  return c.status === 'gathering';
}

/**
 * An "expiring window" badge when a live ceremony is close to its deadline
 * (content-guide §11), or null. Drives off the ceremony's `expiresAt` — the only
 * window timestamp in the metadata read-path. Default threshold: 5 minutes.
 */
export function expiringBadge(
  ceremonies: ReadonlyArray<Pick<UnsealRequest, 'status' | 'expiresAt'>>,
  thresholdSeconds = 300,
  now = Date.now(),
): { label: string; tone: Tone; tooltip: string } | null {
  const soonest = ceremonies
    .filter((c) => c.status === 'gathering' && c.expiresAt > now)
    .reduce<number | null>((min, c) => (min === null || c.expiresAt < min ? c.expiresAt : min), null);
  if (soonest === null) return null;
  const remaining = Math.round((soonest - now) / 1000);
  if (remaining > thresholdSeconds) return null;
  return {
    label: 'Window expiring',
    tone: 'warning',
    tooltip: `An active break-glass window ends in ${windowLabel(remaining)}.`,
  };
}

const UNSEAL_LABEL: Record<UnsealStatus, string> = {
  gathering: 'Gathering shards',
  unlocked: 'Glass broken',
  expired: 'Expired',
  revoked: 'Revoked',
  denied: 'Declined',
};
export function unsealLabel(s: UnsealStatus): string {
  return UNSEAL_LABEL[s] ?? s;
}

/** A short human window from a timeout in seconds (e.g. 1800 → "30m"). */
export function windowLabel(timeoutSeconds: number): string {
  if (timeoutSeconds <= 0) return '—';
  const mins = Math.round(timeoutSeconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = mins / 60;
  return Number.isInteger(hrs) ? `${hrs}h` : `${(timeoutSeconds / 3600).toFixed(1)}h`;
}

/** Count fresh possession proofs across a roster (for the proof summary). */
export function freshProofs(members: RecordMemberView[]): number {
  return members.filter((m) => !m.shardRevoked && m.possessionStatus === 'proven').length;
}

/** Relative time, compact ("2m", "3h", "5d") for audit/proof timestamps. */
export function ago(epochMs: number | null, now = Date.now()): string {
  if (!epochMs) return '—';
  const s = Math.max(0, Math.round((now - epochMs) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * Why convening an unseal is blocked on a record, in plain language — or null if
 * it isn't (M5.2 / M5.4 consequence clarity). Explains the *policy/quorum/role*
 * reason rather than leaving a dead disabled button. Wording stays calm and
 * specific per the content guide; it states the cause, then the path forward.
 */
export function convenenBlockReason(
  r: Pick<RecordSummary, 'status' | 'thresholdK' | 'totalN' | 'memberCount'> & {
    isMember?: boolean;
  },
): string | null {
  if (r.status === 'revoked') {
    return 'This record has been revoked, so it can no longer be unsealed.';
  }
  if (r.status === 'redealing') {
    return 'A re-deal is in progress. You can break the glass once new shards are distributed.';
  }
  if (r.isMember === false) {
    return 'Only members of a record can break the glass. Ask a member, or check you’re signed in as the right identity.';
  }
  if (r.memberCount < r.thresholdK) {
    return `Only ${r.memberCount} of the ${r.thresholdK} members needed have a live shard — quorum can’t be reached yet. Re-deal to restore the margin.`;
  }
  return null;
}

/** A record's display name. The read-path carries `description` (no `name`). */
export function recordName(r: { description: string; id: string }): string {
  return r.description?.trim() || `Record ${r.id.slice(0, 8)}`;
}
